/**
 * Board Component
 *
 * React component that renders the card table and handles user interactions.
 * Uses CardTableBridge to sync boardgame.io state with cards.js visuals.
 */

import React, { useEffect, useRef, useMemo, useState } from 'react';
import CardTableBridge from './CardTableBridge.js';
import {
  getAvailableMoves,
  canEndTurn,
  getEmptyDefenderSlots,
  getAttackableDefenders,
  getAttackableGenerals,
  canDrawBonusCard,
} from '../game/availableMoves.js';
import RematchModal from '../client/RematchModal.jsx';
import BottomButtons from '../components/BottomButtons.jsx';
import { useTranslation } from '../i18n/index.js';
import './Board.css';

// Import image so Parcel bundles it and provides the correct URL
import cardsImageUrl from 'url:../assets/cards.png';
import { VERSION } from '../version.js';

/**
 * Compute which containers and cards should be highlighted based on game state.
 * @param {object} G - Game state
 * @param {object} ctx - Context with currentPlayer
 * @param {string} playerID - This player's ID
 * @param {string|null} selectedHandCard - Currently selected hand card
 * @param {Array} availableMoves - Available moves from state observer
 * @returns {Object} { containers: string[], selected: string|null }
 */
function getHighlightedContainers(G, ctx, playerID, selectedHandCard, availableMoves) {
  const highlighted = {
    containers: [],
    selected: null,
  };

  if (ctx.currentPlayer !== playerID) return highlighted;
  if (G.setupPhase !== 'play') return highlighted;

  const canDoMove = (type) => availableMoves.some((m) => m.type === type);

  if (!selectedHandCard) {
    // No card selected - highlight single-click targets and hand cards
    if (canDoMove('draw2Cards') || canDoMove('draw1Card') || canDoMove('drawBonusCard')) {
      highlighted.containers.push(`stack_${playerID}`);
    }
    if (canDoMove('unstashCard')) {
      highlighted.containers.push(`stash_${playerID}`);
    }
    // Highlight hand if any two-step move available
    if (
      canDoMove('createDefender') ||
      canDoMove('attackDefender') ||
      canDoMove('attackGeneral') ||
      canDoMove('stashCard') ||
      canDoMove('replaceDefenderWithAce') ||
      canDoMove('replaceOwnGeneralWithAce')
    ) {
      highlighted.containers.push(`hand_${playerID}`);
    }
  } else {
    // Card selected - highlight valid targets
    highlighted.selected = selectedHandCard;

    // Empty defender slots (createDefender)
    if (canDoMove('createDefender')) {
      const move = availableMoves.find((m) => m.type === 'createDefender');
      if (move && move.slots) {
        move.slots.forEach((slot) => {
          highlighted.containers.push(`defender${slot}_${playerID}`);
        });
      }
    }

    // Opponent defenders (attackDefender only)
    if (canDoMove('attackDefender')) {
      const move = availableMoves.find((m) => m.type === 'attackDefender');
      if (move && move.targets) {
        move.targets.forEach((t) => {
          highlighted.containers.push(`defender${t.slot}_${t.playerId}`);
        });
      }
    }

    // All defenders for Ace replacement (includes own defenders)
    // Use regex to match only Aces (s1, h1, d1, c1) not Jacks (s11, h11, d11, c11)
    const isAce = /^[shdc]1$/.test(selectedHandCard);
    if (isAce && canDoMove('replaceDefenderWithAce')) {
      const move = availableMoves.find((m) => m.type === 'replaceDefenderWithAce');
      if (move && move.targets) {
        move.targets.forEach((t) => {
          highlighted.containers.push(`defender${t.slot}_${t.playerId}`);
        });
      }
    }

    // Opponent generals (attackGeneral)
    if (canDoMove('attackGeneral')) {
      const move = availableMoves.find((m) => m.type === 'attackGeneral');
      if (move && move.targets) {
        move.targets.forEach((t) => {
          highlighted.containers.push(`general_${t.playerId}`);
        });
      }
    }

    // Stash (stashCard)
    if (canDoMove('stashCard')) {
      highlighted.containers.push(`stash_${playerID}`);
    }

    // Own general for Ace replacement (self-saving)
    // isAce already declared above
    if (isAce && canDoMove('replaceOwnGeneralWithAce')) {
      highlighted.containers.push(`general_${playerID}`);
    }
  }

  return highlighted;
}

/**
 * Run the animated dealing sequence.
 * Calls dealStep move repeatedly with 500ms delays.
 */
function runDealingSequence(moves, numPlayers, onComplete) {
  // 5 steps per player: draw1, draw2, draw3, general, defenders
  const totalSteps = numPlayers * 5;
  let stepCount = 0;

  const executeStep = () => {
    if (stepCount < totalSteps) {
      moves.dealStep();
      stepCount++;
      setTimeout(executeStep, 500);
    } else if (onComplete) {
      onComplete();
    }
  };

  // Start with a small delay
  setTimeout(executeStep, 500);
}

/**
 * Run the animated redistribution sequence after a player defeat.
 * Calls redistributeStep move repeatedly with 300ms delays.
 * Initial 800ms delay allows cards to animate to central stack first.
 */
function runRedistributionSequence(moves, totalCards, onComplete) {
  let stepCount = 0;

  const executeStep = () => {
    if (stepCount <= totalCards) {
      moves.redistributeStep();
      stepCount++;
      setTimeout(executeStep, 300);
    } else if (onComplete) {
      onComplete();
    }
  };

  // Initial delay for collection animation to central stack
  setTimeout(executeStep, 800);
}

/**
 * Run the animated game ending sequence.
 * Calls gameEndingStep move repeatedly to move hand cards to stacks.
 */
function runGameEndingSequence(moves, totalCards, onComplete) {
  let stepCount = 0;

  const executeStep = () => {
    if (stepCount <= totalCards) {
      moves.gameEndingStep();
      stepCount++;
      setTimeout(executeStep, 250);
    } else if (onComplete) {
      onComplete();
    }
  };

  // Initial delay
  setTimeout(executeStep, 500);
}

/**
 * Run the reveal animation sequence for defender attacks.
 * Advances through phases with appropriate timing:
 * - revealing: Card at center (700ms pause)
 * - fanning: Card fanned on defender (300ms)
 * - collapsing: Fan collapses to stack (500ms) - only if willDefeat
 * - pausing: Collapsed stack pauses (1000ms) - only if willDefeat
 */
function runRevealSequence(G, moves, onComplete) {
  if (!G.revealState) return;

  const { phase, willDefeat } = G.revealState;

  const timings = {
    reveal: 700,      // Pause at center to show card
    fan: 300,         // Time to see fanned cards
    collapse: 500,    // Slow collapse animation
    pause: 1000,      // Pause showing stacked cards
  };

  switch (phase) {
    case 'revealing':
      // Card is at center reveal area, wait then advance to fanning
      setTimeout(() => moves.advanceRevealPhase(), timings.reveal);
      break;

    case 'fanning':
      // Card is fanned on defender
      if (willDefeat) {
        // Wait briefly, then trigger collapse
        setTimeout(() => moves.advanceRevealPhase(), timings.fan);
      } else {
        // Attack doesn't defeat - done, just advance to clear state
        setTimeout(() => {
          moves.advanceRevealPhase();
          if (onComplete) onComplete();
        }, timings.fan);
      }
      break;

    case 'collapsing':
      // Cards are collapsing (sync renders with collapsed=true)
      setTimeout(() => moves.advanceRevealPhase(), timings.collapse);
      break;

    case 'pausing':
      // Collapsed stack pausing before moving to attacker's stack
      setTimeout(() => {
        moves.advanceRevealPhase();  // Executes defeat logic, clears state
        if (onComplete) onComplete();
      }, timings.pause);
      break;
  }
}

/**
 * Board receives these props from boardgame.io Client:
 * - G: Game state
 * - ctx: Context (currentPlayer, numPlayers, phase, gameover)
 * - moves: Object with callable move functions
 * - playerID: This player's ID string
 * - isActive: Whether it's this player's turn
 * - isConnected: Connection status for multiplayer
 * - chatMessages: Array of chat messages (for rematch coordination)
 * - sendChatMessage: Function to send chat messages
 * - serverUrl: Server URL for creating new matches
 * - onJoinNewMatch: Callback when joining a new match (rematch)
 * - playerNames: Map of playerID to display name
 */
const Board = ({ G, ctx, moves, playerID, isActive, isConnected, chatMessages, sendChatMessage, serverUrl, onJoinNewMatch, playerNames }) => {
  const { t } = useTranslation();
  const bridgeRef = useRef(null);
  const tableRef = useRef(null);
  const movesRef = useRef(moves);
  const ctxRef = useRef(ctx);

  // State for two-step card selection
  const [selectedHandCard, setSelectedHandCard] = useState(null);
  const selectedHandCardRef = useRef(null);

  // State for rematch modal
  const [showRematchModal, setShowRematchModal] = useState(false);

  // Keep refs updated for use in callbacks
  movesRef.current = moves;
  ctxRef.current = ctx;
  selectedHandCardRef.current = selectedHandCard;

  // Helper to check if it's this player's turn (more reliable than isActive prop)
  const isMyTurnRef = useRef(() => ctxRef.current?.currentPlayer === playerID);
  isMyTurnRef.current = () => ctxRef.current?.currentPlayer === playerID;

  // Get numPlayers safely
  const numPlayers = ctx?.numPlayers || 2;

  // Helper to get player display name
  const getPlayerName = (id) => playerNames?.[id] || `Player ${id}`;

  // Initialize bridge on mount
  useEffect(() => {
    if (!tableRef.current) return;

    // Create bridge instance
    bridgeRef.current = new CardTableBridge({
      tableSelector: `#card-table-${playerID}`,
    });

    // Initialize cards.js
    bridgeRef.current.init({
      cardsUrl: cardsImageUrl,
    });

    // Create layout based on number of players
    bridgeRef.current.createLayout(numPlayers, playerID);

    // Build list of opponent IDs for click handlers
    const opponentIds = [];
    for (let i = 0; i < numPlayers; i++) {
      if (String(i) !== playerID) {
        opponentIds.push(String(i));
      }
    }

    // Hand card click - select for two-step moves
    bridgeRef.current.registerClickHandler(`hand_${playerID}`, (cardId) => {
      if (!isMyTurnRef.current()) return;
      if (!cardId) return; // Ignore placeholder clicks on hand

      if (selectedHandCardRef.current === cardId) {
        // Clicking selected card deselects it
        setSelectedHandCard(null);
      } else {
        // Select this card for two-step move
        setSelectedHandCard(cardId);
      }
    });

    // Stack click - draw cards (single-click move)
    // Handles bonus draw, normal draw, and post-unstash draw
    bridgeRef.current.registerClickHandler(`stack_${playerID}`, () => {
      if (!isMyTurnRef.current()) return;
      setSelectedHandCard(null); // Clear selection
      // Try moves in priority order - only one will succeed
      movesRef.current.drawBonusCard();
      movesRef.current.draw2Cards();
      movesRef.current.draw1Card();
    });

    // Stash click - unstash OR stash selected card
    bridgeRef.current.registerClickHandler(`stash_${playerID}`, () => {
      if (!isMyTurnRef.current()) return;

      if (selectedHandCardRef.current) {
        // Stash the selected card
        movesRef.current.stashCard(selectedHandCardRef.current);
        setSelectedHandCard(null);
      } else {
        // Try to unstash
        movesRef.current.revealStash();
      }
    });

    // Own defender slot click - create defender OR replace with Ace
    [1, 2].forEach((slot) => {
      bridgeRef.current.registerClickHandler(`defender${slot}_${playerID}`, () => {
        if (!isMyTurnRef.current()) return;

        if (selectedHandCardRef.current) {
          const card = selectedHandCardRef.current;
          // Use regex to match only Aces (s1, h1, d1, c1) not Jacks (s11, h11, d11, c11)
          if (/^[shdc]1$/.test(card)) {
            // Ace: try replace defender first (if exists), then create (if slot empty)
            movesRef.current.replaceDefenderWithAce(card, playerID, slot);
            movesRef.current.createDefender(card, slot);
          } else {
            // Non-ace: create defender (only works if slot empty)
            movesRef.current.createDefender(card, slot);
          }
          setSelectedHandCard(null);
        }
      });
    });

    // Opponent defender click handlers (for attacking defenders)
    opponentIds.forEach((oppId) => {
      [1, 2].forEach((slot) => {
        bridgeRef.current.registerClickHandler(`defender${slot}_${oppId}`, () => {
          if (!isMyTurnRef.current() || !selectedHandCardRef.current) return;
          const card = selectedHandCardRef.current;
          // Use startDefenderAttack for reveal animation sequence
          // Note: Aces are just normal attackers against opponent defenders
          movesRef.current.startDefenderAttack(card, oppId, slot);
          setSelectedHandCard(null);
        });
      });

      // Opponent general click handler (for startGeneralAttack - uses reveal animation)
      bridgeRef.current.registerClickHandler(`general_${oppId}`, () => {
        if (!isMyTurnRef.current() || !selectedHandCardRef.current) return;
        movesRef.current.startGeneralAttack(selectedHandCardRef.current, oppId);
        setSelectedHandCard(null);
      });
    });

    // Own general click handler (for ace replacement - self-saving)
    bridgeRef.current.registerClickHandler(`general_${playerID}`, () => {
      if (!isMyTurnRef.current() || !selectedHandCardRef.current) return;
      const card = selectedHandCardRef.current;
      // Use regex to match only Aces (s1, h1, d1, c1) not Jacks (s11, h11, d11, c11)
      if (/^[shdc]1$/.test(card)) {
        // Ace - replace own general
        movesRef.current.replaceOwnGeneralWithAce(card);
        setSelectedHandCard(null);
      }
    });

    // Central stack click - initiates game (Player 0 only)
    bridgeRef.current.registerClickHandler('centralStack', () => {
      if (playerID === '0') {
        movesRef.current.initiateGame();
      }
    });

    // Cleanup on unmount
    return () => {
      if (bridgeRef.current) {
        bridgeRef.current.destroy();
        bridgeRef.current = null;
      }
    };
  }, [playerID, numPlayers]);

  // Sync state whenever G changes
  useEffect(() => {
    if (bridgeRef.current) {
      bridgeRef.current.sync(G);
    }
  }, [G]);

  // Watch for phase transitions to trigger dealing animation
  useEffect(() => {
    if (G.setupPhase === 'distributing' && playerID === '0') {
      // Wait 1 second for distribution animation, then start dealing
      const timer = setTimeout(() => {
        moves.startDealing();
        runDealingSequence(moves, numPlayers);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [G.setupPhase, playerID, moves, numPlayers]);

  // Track if redistribution is already running to prevent duplicate sequences
  const redistributionRunningRef = useRef(false);

  // Track if game ending animation is already running
  const gameEndingRunningRef = useRef(false);

  // Track reveal animation state
  const revealRunningRef = useRef(false);
  const lastRevealPhaseRef = useRef(null);

  // Watch for redistribution phase to trigger card redistribution animation
  useEffect(() => {
    // The first remaining player drives the redistribution (not hardcoded to Player 0)
    // This ensures redistribution works even if Player 0 is defeated or disconnected
    // All players still SEE the animation via state sync - only the driver CALLS the moves
    const driverPlayer = G.redistributionState?.remainingPlayers?.[0];
    const shouldDrive = G.setupPhase === 'redistributing' && playerID === driverPlayer;

    if (shouldDrive && !redistributionRunningRef.current) {
      const totalCards = G.redistributionState?.collectedCards?.length || 0;
      if (totalCards > 0) {
        redistributionRunningRef.current = true;
        // Use movesRef to avoid re-triggering on moves object changes
        runRedistributionSequence(movesRef.current, totalCards, () => {
          redistributionRunningRef.current = false;
        });
      }
    }
    // Reset ref when exiting redistribution phase
    if (G.setupPhase !== 'redistributing') {
      redistributionRunningRef.current = false;
    }
  }, [G.setupPhase, playerID, G.redistributionState?.remainingPlayers?.[0]]); // Include driver player

  // Watch for gameEnding phase to trigger final animation
  useEffect(() => {
    if (G.setupPhase !== 'gameEnding' || gameEndingRunningRef.current) return;

    // Calculate total hand cards across all players
    const totalCards = Object.values(G.players)
      .reduce((sum, p) => sum + (p.hand?.length || 0), 0);

    if (totalCards === 0 && G.gameEndingState) {
      // No cards to animate, just finish
      movesRef.current.gameEndingStep();
      return;
    }

    // Only the winner drives the animation to avoid duplicates
    if (playerID === G.gameEndingState?.winnerId) {
      gameEndingRunningRef.current = true;
      runGameEndingSequence(movesRef.current, totalCards, () => {
        gameEndingRunningRef.current = false;
      });
    }

    // Reset ref when exiting gameEnding phase
    if (G.setupPhase !== 'gameEnding') {
      gameEndingRunningRef.current = false;
    }
  }, [G.setupPhase, playerID, G.gameEndingState?.winnerId]);

  // Watch for reveal phase to trigger attack reveal animation
  useEffect(() => {
    if (G.setupPhase !== 'revealing' || !G.revealState) {
      // Reset refs when exiting revealing phase
      revealRunningRef.current = false;
      lastRevealPhaseRef.current = null;
      return;
    }

    // Only the attacker drives the animation
    if (playerID !== G.revealState.attackerId) return;

    // Avoid re-triggering for the same phase
    if (lastRevealPhaseRef.current === G.revealState.phase) return;
    lastRevealPhaseRef.current = G.revealState.phase;

    revealRunningRef.current = true;
    runRevealSequence(G, movesRef.current, () => {
      revealRunningRef.current = false;
    });
  }, [G.setupPhase, G.revealState?.phase, G.revealState?.attackerId, playerID]);

  // UI state
  const isMyTurn = playerID === ctx.currentPlayer;

  // Compute available moves for current player
  const availableMoves = useMemo(
    () => getAvailableMoves(G, ctx, playerID),
    [G, ctx, playerID]
  );

  // Determine if overlay should be shown (only during play phase for current player)
  // Overlay is hidden during reveal animation to avoid z-index conflicts with animating cards
  const showOverlay = isMyTurn && G.setupPhase === 'play';

  // Update highlights when selection or available moves change
  useEffect(() => {
    if (!bridgeRef.current) return;

    if (!showOverlay) {
      bridgeRef.current.clearHighlights();
      return;
    }

    const highlights = getHighlightedContainers(G, ctx, playerID, selectedHandCard, availableMoves);
    bridgeRef.current.applyHighlights(highlights);
  }, [G, ctx, playerID, selectedHandCard, availableMoves, showOverlay]);

  // Clear selection when turn ends
  useEffect(() => {
    if (!isMyTurn) {
      setSelectedHandCard(null);
    }
  }, [isMyTurn]);

  // Clear selection when game state changes (move was executed)
  useEffect(() => {
    // If hand no longer contains selected card, clear selection
    const player = G.players?.[playerID];
    if (selectedHandCard && player && !player.hand.includes(selectedHandCard)) {
      setSelectedHandCard(null);
    }
  }, [G, playerID, selectedHandCard]);

  const gameOver = ctx.gameover;

  // Show rematch modal when game ends (online mode only)
  useEffect(() => {
    if (gameOver && sendChatMessage) {
      setShowRematchModal(true);
    }
  }, [gameOver, sendChatMessage]);

  // Helper to check if a move type is available
  const canDoMove = (type) => availableMoves.some(m => m.type === type);

  // End turn handler
  const handleEndTurn = () => {
    if (canEndTurn(G, playerID)) {
      moves.endTurn();
    }
  };

  return (
    <div className="waterloo-board">
      <div className="game-info">
        <span className="player-id">{getPlayerName(playerID)}</span>
        <span className={`turn-status ${isMyTurn ? 'active' : ''}`}>
          {isMyTurn ? t('game.yourTurn') : t('game.waitingFor', { playerName: getPlayerName(ctx.currentPlayer) })}
        </span>
        {!isConnected && <span className="connection-status">{t('game.connecting')}</span>}
        {gameOver && !showRematchModal && (
          <span className="game-over">
            {gameOver.winner !== undefined
              ? t('game.winner', { playerName: getPlayerName(gameOver.winner) })
              : t('game.draw')}
          </span>
        )}
      </div>

      <div className="game-stats">
        <span>{t('stats.phase')}: {G.setupPhase || t('stats.unknown')}</span>
        <span>{t('stats.central')}: {G.centralStack?.length || 0}</span>
        <span>{t('stats.stack')}: {G.players?.[playerID]?.stack?.length || 0}</span>
        <span>{t('stats.hand')}: {G.players?.[playerID]?.hand?.length || 0}</span>
      </div>

      {/* Card table container - cards.js renders here */}
      <div
        id={`card-table-${playerID}`}
        className={`card-table ${showOverlay ? 'overlay-active' : ''}`}
        ref={tableRef}
      >
        {/* Dark overlay for highlighting - fades in/out */}
        <div className={`card-table-overlay ${showOverlay ? 'visible' : ''}`} />
        {/* Connection lost overlay */}
        {isConnected === false && (
          <div className="connection-overlay">
            <span>{t('game.reconnecting')}</span>
          </div>
        )}
      </div>

      <div className="action-hint">
        {G.setupPhase === 'waiting' && playerID === '0' && (
          t('phase.clickToStart')
        )}
        {G.setupPhase === 'waiting' && playerID !== '0' && (
          t('phase.waitingForStart', { playerName: getPlayerName('0') })
        )}
        {G.setupPhase === 'distributing' && (
          t('phase.distributing')
        )}
        {G.setupPhase === 'dealing' && (
          t('phase.dealing', { playerName: getPlayerName(G.currentSetupPlayer) })
        )}
        {G.setupPhase === 'redistributing' && (
          t('phase.redistributing', { playerName: getPlayerName(G.redistributionState?.defeatedPlayerId) })
        )}
        {G.setupPhase === 'gameEnding' && (
          t('phase.gameEnding')
        )}
        {G.setupPhase === 'finished' && gameOver && (
          gameOver.winner !== undefined
            ? t('game.wins', { playerName: getPlayerName(gameOver.winner) })
            : t('game.gameEnded')
        )}
        {G.setupPhase === 'play' && isMyTurn && !selectedHandCard && (
          <span>
            {G.turnState.bonusDrawAllowed && t('hint.bonusExposed') + ' '}
            {G.turnState.isPriorityTurn && !G.turnState.hasDrawn && t('hint.priorityTurn') + ' '}
            {canDoMove('draw2Cards') && (G.turnState.isPriorityTurn ? t('hint.draw3Cards') + ' ' : t('hint.draw2Cards') + ' ')}
            {canDoMove('draw1Card') && (G.turnState.isPriorityTurn ? t('hint.draw2Cards') + ' ' : t('hint.draw1Card') + ' ')}
            {canDoMove('unstashCard') && t('hint.unstash') + ' '}
            {(canDoMove('createDefender') || canDoMove('attackDefender') || canDoMove('attackGeneral') || canDoMove('stashCard') || canDoMove('replaceOwnGeneralWithAce')) &&
              t('hint.selectCard') + ' '}
          </span>
        )}
        {G.setupPhase === 'play' && isMyTurn && selectedHandCard && (
          <span>
            {t('hint.cardSelected')}
          </span>
        )}
        {G.setupPhase === 'play' && !isMyTurn && (
          t('hint.waitingForPlayer', { playerName: getPlayerName(ctx.currentPlayer) })
        )}
      </div>

      {/* End Turn button - only visible when hand is empty */}
      {G.setupPhase === 'play' && isMyTurn && canDoMove('endTurn') && (
        <button className="end-turn-button" onClick={handleEndTurn}>
          {t('button.endTurn')}
        </button>
      )}

      <BottomButtons />

      {/* Rematch modal for online mode */}
      {showRematchModal && sendChatMessage && (
        <RematchModal
          serverUrl={serverUrl}
          playerID={playerID}
          numPlayers={ctx.numPlayers}
          chatMessages={chatMessages || []}
          sendChatMessage={sendChatMessage}
          onJoinMatch={onJoinNewMatch}
          gameOver={gameOver}
          playerNames={playerNames}
        />
      )}
    </div>
  );
};

export default Board;
